import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { measurementKeys } from './measurements.api';
import { watchSession } from './sessionStream';

export type SessionTransport = 'stream' | 'polling';

/**
 * Keeps the desk's copy of a session current.
 *
 * Every event is a prompt to refetch the session through the ordinary API — the stream never
 * carries a candidate or a measurement itself. Only while `enabled`, so each open dialog holds one
 * stream and a closed one holds none.
 */
export function useSessionStream(sessionId: string | null, enabled: boolean): SessionTransport {
  const queryClient = useQueryClient();
  const [transport, setTransport] = useState<SessionTransport>('stream');

  useEffect(() => {
    if (!sessionId || !enabled) return undefined;

    const controller = new AbortController();
    setTransport('stream');

    void watchSession(sessionId, {
      signal: controller.signal,
      onStatus: () =>
        void queryClient.invalidateQueries({ queryKey: measurementKeys.session(sessionId) }),
      onFallback: () => setTransport('polling'),
    });

    return () => controller.abort();
  }, [sessionId, enabled, queryClient]);

  return transport;
}
